import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Empty, message } from 'antd';
import { ArrowLeftOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import TopBar from '@/Components/TopBar';
import Navbar from "@/Components/navbar";
import { getUserJobInteractionCounts, getUserJobInteractions } from '@/services/jobs';
import './index.less';

// 用户计算次数统计项
interface UserCount {
    user_id: number;
    username: string;
    count: number;
}

// 用户提交的任务记录项
interface InteractionItem {
    id: number;
    user_id: number;
    username: string;
    job_id: number;
    subjob_id: number | null;
    created_at: string;
    updated_at: string;
    job_name: string | null;
    subjob_name: string | null;
}

const PAGE_SIZE = 10;

// 后端返回 UTC 时间，统一 +8 小时展示
const formatTime = (t?: string | null) =>
    t ? dayjs(t).add(8, 'hours').format('YYYY-MM-DD HH:mm:ss') : '-';

const Component = () => {
    // 用户计算次数统计
    const [counts, setCounts] = useState<UserCount[]>([]);
    const [countsLoading, setCountsLoading] = useState(false);

    // 选中用户的提交记录
    const [selectedUser, setSelectedUser] = useState<UserCount | null>(null);
    const [items, setItems] = useState<InteractionItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [detailLoading, setDetailLoading] = useState(false);

    // 加载用户计算次数统计（仅保留 user001 ~ user010，按计算次数从多到少排序）
    const loadCounts = useCallback(async () => {
        setCountsLoading(true);
        try {
            const res = await getUserJobInteractionCounts();
            const list: UserCount[] = Array.isArray(res.data) ? res.data : [];
            // 只取编号 1-10 的用户（user001 ~ user010）
            const firstTen = list.filter((u) => {
                const m = /^user(\d+)$/.exec(u.username || '');
                if (!m) return false;
                const n = Number(m[1]);
                return n >= 1 && n <= 10;
            });
            setCounts([...firstTen].sort((a, b) => (b.count || 0) - (a.count || 0)));
            //  setCounts([...list].sort((a, b) => (b.count || 0) - (a.count || 0))); 全部用户
        } catch (e) {
            message.error('加载用户计算次数失败');
        } finally {
            setCountsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCounts();
    }, [loadCounts]);

    // 加载某用户提交的任务记录（分页）
    const loadDetail = useCallback(async (userId: number, nextPage: number) => {
        setDetailLoading(true);
        try {
            const res = await getUserJobInteractions({
                user_id: userId,
                page: nextPage,
                page_size: PAGE_SIZE,
            });
            const data = res.data || {};
            setItems(Array.isArray(data.items) ? data.items : []);
            setTotal(Number(data.total) || 0);
            setPage(Number(data.page) || nextPage);
        } catch (e) {
            message.error('加载用户提交记录失败');
        } finally {
            setDetailLoading(false);
        }
    }, []);

    // 点击用户行，进入该用户的提交记录详情
    const openUser = (user: UserCount) => {
        setSelectedUser(user);
        setItems([]);
        setTotal(0);
        setPage(1);
        loadDetail(user.user_id, 1);
    };

    const backToList = () => {
        setSelectedUser(null);
        setItems([]);
        setTotal(0);
        setPage(1);
    };

    const userColumns: any[] = [
        {
            title: '排名',
            key: 'rank',
            width: 80,
            render: (_: any, __: any, index: number) => index + 1,
        },
        { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 120 },
        { title: '用户名', dataIndex: 'username', key: 'username' },
        {
            title: '计算次数',
            dataIndex: 'count',
            key: 'count',
            width: 160,
            defaultSortOrder: 'descend',
            sorter: (a: UserCount, b: UserCount) => (a.count || 0) - (b.count || 0),
        },
    ];

    const detailColumns: any[] = [
        {
            title: '序号',
            key: 'index',
            width: 70,
            render: (_: any, __: any, index: number) => (page - 1) * PAGE_SIZE + index + 1,
        },
        { title: '任务名称', dataIndex: 'job_name', key: 'job_name', render: (v: any) => v || '-' },
        { title: '子任务名称', dataIndex: 'subjob_name', key: 'subjob_name', render: (v: any) => v || '-' },
        { title: '任务ID', dataIndex: 'job_id', key: 'job_id', width: 100 },
        { title: '子任务ID', dataIndex: 'subjob_id', key: 'subjob_id', width: 110, render: (v: any) => (v ?? '-') },
        { title: '提交时间', dataIndex: 'created_at', key: 'created_at', width: 180, render: (v: any) => formatTime(v) },
    ];

    return (
        <>
            <TopBar title="用户行为分析" mainContent={<Navbar />} />
            <div className="user-info-page">
                {!selectedUser ? (
                    <>
                        <div className="user-info-header">
                            <UserOutlined className="user-info-header-icon" />
                            用户数据统计
                        </div>
                        <Table<UserCount>
                            className="user-info-count-table"
                            rowKey="user_id"
                            columns={userColumns}
                            dataSource={counts}
                            loading={countsLoading}
                            pagination={false}
                            locale={{ emptyText: <Empty description="暂无数据" /> }}
                            onRow={(record) => ({
                                onClick: () => openUser(record),
                            })}
                        />
                    </>
                ) : (
                    <>
                        <Button
                            className="user-info-back-btn"
                            type="link"
                            icon={<ArrowLeftOutlined />}
                            onClick={backToList}
                        >
                            返回用户列表
                        </Button>
                        <div className="user-info-detail-title">
                            {selectedUser.username}（ID: {selectedUser.user_id}）的任务提交记录
                            <span className="user-info-total">
                                共 {total} 条
                            </span>
                        </div>
                        <Table<InteractionItem>
                            rowKey="id"
                            columns={detailColumns}
                            dataSource={items}
                            loading={detailLoading}
                            pagination={{
                                current: page,
                                pageSize: PAGE_SIZE,
                                total,
                                showSizeChanger: false,
                                onChange: (p) => loadDetail(selectedUser.user_id, p),
                            }}
                            locale={{ emptyText: <Empty description="暂无数据" /> }}
                        />
                    </>
                )}
            </div>
        </>
    );
};

export default Component;
